import { ExternalLink, Text } from '@ui/themed';
import { t } from 'i18next';
import type { PropsWithChildren } from 'react';
import type { Env } from '../../../context/env-context';
import { useEnv } from '../../../context/env-context';
import type { I18nPrefix } from '../../../i18n/prefix';
import {
  adminScriptPdpAllVariantsGraphic,
  adminScriptPdpMatrixGraphic,
  adminScriptPdpNoPriceUrl,
  adminScriptPdpUrl,
  adminScriptPlpUrl,
  adminScriptQuickBuyGraphic,
} from '../assets/images';
import {
  buildAllVariantsSnippet,
  buildCardSnippet,
  buildMatrixSnippet,
  buildPdpSnippet,
  buildPriceSnippet,
  buildQuickBuySnippet,
} from '../utils/snippets';
import { CodeSnippet } from './code-snippet';
import { InteractiveExampleLink } from './interactive-example-link';
import { WidgetSection } from './widget-section';

const PAGE = 'features:storefront_widgets.pages.storefront_widgets_page.pdp' satisfies I18nPrefix;

const figmaProto = (nodeId: string) =>
  `https://www.figma.com/proto/5FPCY5hSGl7mlwjsjMZI2t/SparkLayer-Interfaces?page-id=0%3A1&type=design&node-id=${nodeId}&scaling=min-zoom&starting-point-node-id=35%3A7920&hide-ui=1`;

type WidgetKey = 'pdp' | 'all_variants' | 'matrix' | 'cards' | 'price' | 'quick_buy';

type Widget = {
  key: WidgetKey;
  buildSnippet: (platform: Env['platform']) => string;
  image: string;
  guide: string;
  exampleLink?: string;
};

const PDP_WIDGETS: Widget[] = [
  {
    key: 'pdp',
    buildSnippet: buildPdpSnippet,
    image: adminScriptPdpUrl,
    guide: 'https://docs.sparklayer.io/product-detail-interface',
    exampleLink: figmaProto('86-36708'),
  },
  {
    key: 'all_variants',
    buildSnippet: buildAllVariantsSnippet,
    image: adminScriptPdpAllVariantsGraphic,
    guide: 'https://docs.sparklayer.io/product-display#8nt28',
    exampleLink: figmaProto('63-17387'),
  },
  {
    key: 'matrix',
    buildSnippet: buildMatrixSnippet,
    image: adminScriptPdpMatrixGraphic,
    guide: 'https://docs.sparklayer.io/product-display#N1epz',
  },
];

const CARD_WIDGETS: Widget[] = [
  {
    key: 'cards',
    buildSnippet: buildCardSnippet,
    image: adminScriptPlpUrl,
    guide: 'https://docs.sparklayer.io/product-card-interface',
    exampleLink: figmaProto('58-14870'),
  },
  {
    key: 'price',
    buildSnippet: buildPriceSnippet,
    image: adminScriptPdpNoPriceUrl,
    guide: 'https://docs.sparklayer.io/product-price-interface',
    exampleLink: figmaProto('86-33700'),
  },
  {
    key: 'quick_buy',
    buildSnippet: buildQuickBuySnippet,
    image: adminScriptQuickBuyGraphic,
    guide: 'https://docs.sparklayer.io/product-display#d5EuU',
  },
];

function SnippetWithPreview({
  code,
  image,
  exampleLink,
  children,
}: PropsWithChildren<{ code: string; image: string; exampleLink?: string }>) {
  return (
    <div className="flex flex-col gap-2">
      {children}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1">
          <CodeSnippet code={code} />
        </div>
        <div className="relative hidden w-2/5 max-w-md shrink-0 lg:block">
          <img src={image} alt="" className="w-full rounded-md shadow" />
          {exampleLink && (
            <div className="absolute end-2 top-2">
              <InteractiveExampleLink link={exampleLink} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WidgetPreview({ widget, platform }: { widget: Widget; platform: Env['platform'] }) {
  return (
    <SnippetWithPreview
      code={widget.buildSnippet(platform)}
      image={widget.image}
      exampleLink={widget.exampleLink}
    >
      <Text size="sm" variant="secondary">
        {t(`${PAGE}.sections.${widget.key}.description`)}{' '}
        <ExternalLink link={widget.guide}>{t(`${PAGE}.sections.${widget.key}.guide`)}</ExternalLink>
      </Text>
    </SnippetWithPreview>
  );
}

export function PdpTab() {
  const env = useEnv();

  return (
    <div className="flex flex-col gap-5">
      <WidgetSection title={t(`${PAGE}.sections.pdp.title`)}>
        {env.platform === 'woocommerce' && (
          <>
            <Text size="sm" variant="secondary">
              {t(`${PAGE}.woocommerce_notice_1`)}
            </Text>
            <Text size="sm" variant="secondary">
              {t(`${PAGE}.woocommerce_notice_2`)}
            </Text>
          </>
        )}

        {PDP_WIDGETS.map((widget) => (
          <WidgetPreview key={widget.key} widget={widget} platform={env.platform} />
        ))}
      </WidgetSection>

      <WidgetSection title={t(`${PAGE}.sections.cards.title`)}>
        {CARD_WIDGETS.map((widget) => (
          <WidgetPreview key={widget.key} widget={widget} platform={env.platform} />
        ))}
      </WidgetSection>
    </div>
  );
}
